import { getChainConfigEntry } from '@/config/chain'
import { formatPercentage } from '@/domain/common/format'
import { sortByAPY, sortByUsdValue } from '@/domain/common/sorters'
import { OpenDialogFunction } from '@/domain/hooks/useDialog'
import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { DialogConfig } from '@/features/dialogs/common/types'
import { useIsolatedPairDetails } from '@/features/isolated-details/logic/useIsolatedPairDetails'
import { lendWithdrawDialogConfig } from '@/features/isolated-dialogs/lend-withdraw/LendWithdrawDialog'
import { withdrawDialogConfig } from '@/features/isolated-dialogs/withdraw/WithdrawDialog'
import { ApyWithRewardsCell } from '@/features/my-portfolio/components/ApyWithRewardsCell'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { TableCell, TableRow } from '@/ui/atoms/table/Table'
import { Typography } from '@/ui/atoms/typography/Typography'
import { ApyTooltip } from '@/ui/molecules/apy-tooltip/ApyTooltip'
import { ActionsCell } from '@/ui/molecules/data-table/components/ActionsCell'
import { CompactValueCell } from '@/ui/molecules/data-table/components/CompactValueCell'
import { TokenWithLogo } from '@/ui/molecules/data-table/components/TokenWithLogo'
import { ColumnDefinition } from '@/ui/molecules/data-table/types'
import { ResponsiveDataTable } from '@/ui/organisms/responsive-data-table/ResponsiveDataTable'
import { cn } from '@/ui/utils/style'
import { useMemo } from 'react'
import { Address } from 'viem'
import { useChainId } from 'wagmi'
import { Deposit } from '../../logic/useMyPortfolio'

export interface MyDepositsTableProps {
  assets: Deposit[]
  openDialog: OpenDialogFunction
}

export function MyDepositsTable({ assets: rawAssets, openDialog }: MyDepositsTableProps) {
  const assets = rawAssets.map((asset) => ({
    ...asset,
    collateralToken: getDisplayToken(asset.chainId, asset.collateralToken),
    borrowToken: getDisplayToken(asset.chainId, asset.borrowToken),
  }))
  const chainId = useChainId()
  const { isolatedPartnerPoints, incentivizedPairs } = getChainConfigEntry(chainId)
  const { getByAddress, error: pointsDataError, isLoading: isPointsDataLoading } = usePointsData()
  const hasPointsError = !isPointsDataLoading && !!pointsDataError?.message

  const columnDefinition = useMemo<{ [key: string]: ColumnDefinition<Deposit> }>(
    () => ({
      collateralAsset: {
        header: 'Asset/Market',
        renderCell: ({ collateralToken, borrowToken }) => (
          <div className="flex flex-col">
            <div className="flex items-center">
              <TokenWithLogo
                token={collateralToken}
                reserveStatus="active"
                isCombinedBalance={false}
                className="pb-0 font-medium text-base"
                hideName
              />
            </div>
            <Typography variant="prompt" className="ml-8">
              Paired with {borrowToken.symbol}
            </Typography>
          </div>
        ),
      },
      deposit: {
        header: 'Deposit',
        sortable: true,
        sortingFn: (a, b) =>
          sortByUsdValue(
            { token: a.original.collateralToken, value: a.original.lendAmount },
            { token: b.original.collateralToken, value: b.original.lendAmount },
            'value',
          ),
        headerAlign: 'right',
        showOnMobile: true,
        renderCell: ({ collateralToken, lendAmount }, mobileViewOptions) => (
          <div className="flex flex-col items-center md:items-end">
            <CompactValueCell
              token={collateralToken}
              value={lendAmount}
              mobileViewOptions={mobileViewOptions}
              hideEmpty
            />
          </div>
        ),
      },
      apy: {
        header: <ApyTooltip variant="supply">APY</ApyTooltip>,
        headerAlign: 'right',
        sortable: true,
        showOnMobile: true,
        sortingFn: (a, b) => sortByAPY(a.original.supplyAPY, b.original.supplyAPY),
        renderCell: ({ supplyAPY, collateralToken, pairAddress, borrowToken, type }, mobileViewOptions) => {
          if (type === 'collateral') {
            return (
              <div
                className={cn(
                  'flex h-full w-full flex-row items-center',
                  mobileViewOptions?.isMobileView && mobileViewOptions.showOnMobile ? 'justify-center' : 'justify-end',
                )}
              >
                —
              </div>
            )
          }
          const cellColorOverride =
            isolatedPartnerPoints[CheckedAddress(pairAddress)]?.options?.cellColorOverrides?.lend
          const extraIncentives = incentivizedPairs[CheckedAddress(pairAddress)]?.lending
          const additionalAPY = extraIncentives
            ? formatPercentage(Percentage(0 / 100)) // Merkl opportunities removed
            : undefined
          const points = getByAddress({
            type: 'isolated',
            action: 'deposit',
            address: pairAddress,
          })

          return (
            <div className="flex justify-center md:justify-end">
              <ApyWithRewardsCell
                action="lend"
                asset={type === 'lending' ? collateralToken.symbol : borrowToken.symbol}
                apy={supplyAPY}
                points={points}
                mobileViewOptions={mobileViewOptions}
                partnerPoints={isolatedPartnerPoints[CheckedAddress(pairAddress)]?.points}
                extraIncentives={extraIncentives}
                additionalAPY={additionalAPY}
                cellColorOverride={cellColorOverride}
                stacked
                pointsError={hasPointsError}
                pointsLoading={isPointsDataLoading}
              />
            </div>
          )
        },
      },
      actions: {
        header: '',
        renderCell: ({ chainId, pairAddress, lendAmount, type }) => (
          <ActionsCellButton
            chainId={chainId}
            pairAddress={pairAddress}
            lendAmount={lendAmount}
            type={type}
            openDialog={openDialog}
          />
        ),
      },
    }),
    [openDialog, isolatedPartnerPoints, incentivizedPairs, getByAddress, hasPointsError, isPointsDataLoading],
  )

  return (
    <Panel className="bg-panel xl:h-full md:px-3">
      <Panel.Header>
        <Panel.Title className="text-xl md:px-3" gradient>
          My deposits
        </Panel.Title>
      </Panel.Header>

      <Panel.Content>
        <ResponsiveDataTable
          gridTemplateColumnsClassName="grid-cols-[2fr_1fr_1fr_1fr] gap-4"
          columnDefinition={columnDefinition}
          data={assets}
          variant="isolated-positions"
          footer={
            assets.length === 0 ? (
              <TableRow className="border-none py-4 pl-0.5 text-sm">
                <TableCell>
                  <Typography variant="prompt" className="text-sm">
                    You have no positions yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : undefined
          }
        />
      </Panel.Content>
    </Panel>
  )
}

function ActionsCellButton({
  chainId,
  pairAddress,
  lendAmount,
  type,
  openDialog,
}: {
  chainId: number
  pairAddress: Address
  lendAmount: NormalizedUnitNumber
  type: string
  openDialog: OpenDialogFunction
}) {
  const pairDetails = useIsolatedPairDetails({
    chainId,
    pairAddress,
  })
  return (
    <ActionsCell className="flex justify-end">
      <Button
        variant="primary"
        size="sm"
        className="w-full md:w-auto"
        disabled={lendAmount.isZero()}
        onClick={() => {
          if (type === 'lending') {
            openDialog(lendWithdrawDialogConfig, { walletOverview: pairDetails.walletOverview })
          } else {
            openDialog(withdrawDialogConfig as DialogConfig<any>, {
              walletOverview: pairDetails.walletOverview,
              pairOverview: pairDetails.pairOverview,
            })
          }
        }}
      >
        Withdraw
      </Button>
    </ActionsCell>
  )
}
